// src/components/admin/scheduling/scheduleSettingsUtils.js

export const DAY_KEYS_SUNDAY_FIRST = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

export const DAY_KEYS_MONDAY_FIRST = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

export const LEGACY_REGULAR_WEEK_CONFIG = Object.freeze({
  monday: { isOpen: true, startMin: 14 * 60, endMin: 21 * 60 },
  tuesday: { isOpen: true, startMin: 14 * 60, endMin: 21 * 60 },
  wednesday: { isOpen: true, startMin: 14 * 60, endMin: 21 * 60 },
  thursday: { isOpen: true, startMin: 14 * 60, endMin: 21 * 60 },
  friday: { isOpen: false, startMin: 14 * 60, endMin: 21 * 60 },
  saturday: { isOpen: true, startMin: 9 * 60, endMin: 17 * 60 },
  sunday: { isOpen: false, startMin: 9 * 60, endMin: 17 * 60 },
});

// ── Active default schedule ──────────────────────────────────────────────────
// The default weekly schedule used for any date without an explicit day-config.
// It starts as LEGACY_REGULAR_WEEK_CONFIG (identical to the previous hardcoded
// behavior) and can be hydrated at runtime from `settings/scheduleDefault` via
// setActiveDefaultWeekConfig(). Every existing resolveScheduleForDate /
// defaultTimelineRangeForDayKey call site picks up the hydrated value with no
// change needed on their end. Kept module-local (not a React state) so pure
// resolver helpers can read it synchronously.
let activeDefaultWeekConfig = LEGACY_REGULAR_WEEK_CONFIG;

export function setActiveDefaultWeekConfig(weekConfig) {
  activeDefaultWeekConfig = normalizeWeekConfig(weekConfig, LEGACY_REGULAR_WEEK_CONFIG);
  return activeDefaultWeekConfig;
}

export function getActiveDefaultWeekConfig() {
  return activeDefaultWeekConfig;
}

export function dayKeyFromDate(dateObj) {
  if (!(dateObj instanceof Date)) return null;
  return DAY_KEYS_SUNDAY_FIRST[dateObj.getDay()] || null;
}

export function clampMinuteValue(value, fallback) {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(0, Math.min(24 * 60, n));
}

export function normalizeDaySchedule(input, fallback = {}) {
  const fallbackStart = clampMinuteValue(fallback?.startMin, 14 * 60);
  const fallbackEnd = clampMinuteValue(fallback?.endMin, Math.max(fallbackStart + 60, 21 * 60));

  const isOpen = input?.isOpen !== false;
  const rawStart = clampMinuteValue(input?.startMin, fallbackStart);
  const rawEnd = clampMinuteValue(input?.endMin, fallbackEnd);
  const startMin = rawStart;
  const endMin = rawEnd > startMin ? rawEnd : Math.min(24 * 60, startMin + 60);

  return { isOpen, startMin, endMin };
}

export function normalizeWeekConfig(weekConfig, fallback = LEGACY_REGULAR_WEEK_CONFIG) {
  const out = {};
  DAY_KEYS_MONDAY_FIRST.forEach((dayKey) => {
    out[dayKey] = normalizeDaySchedule(weekConfig?.[dayKey], fallback?.[dayKey]);
  });
  return out;
}

export function defaultTimelineRangeForDayKey(dayKey) {
  const activeDefault = getActiveDefaultWeekConfig();
  const normalized = normalizeDaySchedule(
    activeDefault?.[dayKey],
    activeDefault?.monday || LEGACY_REGULAR_WEEK_CONFIG.monday
  );
  return { startMin: normalized.startMin, endMin: normalized.endMin };
}

export function resolveScheduleForDate(dateObj, dayConfig = null, fallbackWeekConfig = getActiveDefaultWeekConfig()) {
  const dayKey = dayKeyFromDate(dateObj);
  const fallbackDay = normalizeDaySchedule(
    fallbackWeekConfig?.[dayKey],
    LEGACY_REGULAR_WEEK_CONFIG?.[dayKey]
  );

  const cfgStart = Number(dayConfig?.timelineStartMin);
  const cfgEnd = Number(dayConfig?.timelineEndMin);
  const hasTimelineOverride = Number.isFinite(cfgStart) && Number.isFinite(cfgEnd) && cfgEnd > cfgStart;

  if (dayConfig) {
    const startMin = hasTimelineOverride ? clampMinuteValue(cfgStart, fallbackDay.startMin) : fallbackDay.startMin;
    const endMin = hasTimelineOverride
      ? clampMinuteValue(cfgEnd, Math.max(startMin + 60, fallbackDay.endMin))
      : fallbackDay.endMin;

    return {
      dayKey,
      isClosed: !!dayConfig?.isClosed,
      startMin,
      endMin: endMin > startMin ? endMin : Math.min(24 * 60, startMin + 60),
      source: "day_config",
    };
  }

  return {
    dayKey,
    isClosed: !fallbackDay.isOpen,
    startMin: fallbackDay.startMin,
    endMin: fallbackDay.endMin,
    source: "default",
  };
}

export function buildRegularScheduleTypeDraft() {
  return {
    name: "Regular / Schoolday Schedule",
    description: "Default school-year schedule",
    weekConfig: normalizeWeekConfig(LEGACY_REGULAR_WEEK_CONFIG),
  };
}
